import { ReactNode, useEffect } from 'react';
import {
    Dialog,
    DialogBackdrop,
    DialogPanel,
    DialogTitle,
} from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useBreakpoint } from 'hooks/useBreakPoint';
import { BottomModal } from 'components/BottomModal';
import { FadeMotion } from 'components/motions/fade';

interface DrawerProps {
    isOpen: boolean;
    onClose?: () => void;
    title?: string;
    children: ReactNode;
    showBackDrop?: boolean;
    mobileWrapperClassName?: string;
    size?: 'normal' | 'large' | 'small';
}

export default function Modal({
    isOpen,
    onClose,
    title,
    children,
    showBackDrop = true,
    mobileWrapperClassName = '',
    size = 'normal',
}: DrawerProps) {
    const isDesktop = useBreakpoint('md');

    const handleClose = () => {
        if (onClose) onClose();
        document.body.style.overflow = 'initial';
    };
    useEffect(() => {
        return () => {
            if (isOpen) document.body.style.overflow = 'initial';
        };
    }, []);

    const getSizeClass = {
        normal: 'max-w-xl',
        large: 'max-w-2xl',
        small: 'max-w-md',
    } as const;
    if (!isDesktop)
        return (
            <BottomModal
                showBackDrop={showBackDrop}
                contentWrapperClassName={[
                    'h-max rounded-t-3xl z-[9999]',
                    mobileWrapperClassName,
                ].join(' ')}
                isOpen={isOpen}
                onClose={onClose && handleClose}
            >
                <div className=" w-svw z-10 relative flex-1">
                    {title && (
                        <div className="sticky z-50 top-0 start-0 bg-base-200  w-full pt-3 ">
                            <div className="ms-3 flex h-7 justify-between items-center py-5 w-full pe-6">
                                <span className="truncate text-primary-content">
                                    {title}
                                </span>
                                <button
                                    type="button"
                                    onClick={handleClose}
                                    className="relative ms-4 rounded-md bg-base-200  text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                                >
                                    <span className="absolute -inset-2.5" />
                                    <span className="sr-only">Close panel</span>
                                    <XMarkIcon
                                        aria-hidden="true"
                                        className="h-6 w-6"
                                    />
                                </button>
                            </div>
                        </div>
                    )}
                    <div className="sm:px-6 px-4">{children}</div>
                </div>
            </BottomModal>
        );

    return (
        <Dialog
            open={isOpen}
            onClose={(e) => {
                handleClose();
            }}
            className={['relative z-50'].join(' ')}
            title={title}
        >
            <div onClick={(e) => e.stopPropagation()}>
                <div className="fixed inset-0" />
                {showBackDrop && (
                    <DialogBackdrop
                        transition
                        className="fixed inset-0 bg-gray-300  dark:bg-modal-backdrop-dark  dark:backdrop-blur-[2px] bg-opacity-75 transition-opacity duration-300 ease-in-out data-[closed]:opacity-0"
                    />
                )}
                {/* Centered Modal */}
                <div className="fixed inset-0 flex items-center justify-center">
                    <DialogPanel
                        transition
                        className={`bg-base-200  shadow-xl md:rounded-3xl w-full ${getSizeClass[size]} overflow-hidden`}
                    >
                        <FadeMotion>
                            <div className="flex flex-col h-full overflow-y-scroll ">
                                <div className="z-10 relative flex-1    overflow-x-hidden overflow-y-auto">
                                    {children}
                                </div>
                            </div>
                        </FadeMotion>
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    );
}
